import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import ApplicationForm from './ApplicationForm';
import ApplicationLoginForm from './ApplicationLoginForm';
import LMSLoginHandler from './LMSLoginHandler';
import authService from '../services/authService';

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [showApplicationModal, setShowApplicationModal] = useState(false);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [showLMSLogin, setShowLMSLogin] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [userData, setUserData] = useState(null);
  const [hasLMSAccess, setHasLMSAccess] = useState(false);
  const location = useLocation();

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
    // Toggle mobile-nav-active class on body (like PHP version)
    document.body.classList.toggle('mobile-nav-active');
  };

  const closeMenu = () => {
    setIsMenuOpen(false);
    // Remove mobile-nav-active class from body
    document.body.classList.remove('mobile-nav-active');
  };

  const toggleDropdown = (e) => {
    e.preventDefault();
    const dropdown = e.target.closest('.dropdown');
    if (dropdown) {
      dropdown.classList.toggle('active');
      const dropdownMenu = dropdown.querySelector('ul');
      if (dropdownMenu) {
        dropdownMenu.classList.toggle('dropdown-active');
      }
    }
  };

  const isActive = (path) => {
    return location.pathname === path ? 'active' : '';
  };

  // Check authentication status
  useEffect(() => {
    const checkAuthStatus = () => {
      const authStatus = authService.isAuthenticated();
      const user = authService.getUserData();
      setIsAuthenticated(authStatus);
      setUserData(user);
      
      // Check LMS access
      if (user && user.lmsDetails) {
        const { lmsDetails } = user;
        const hasCredentials = lmsDetails.un && lmsDetails.pwd && 
                               lmsDetails.un.trim() !== '' && lmsDetails.pwd.trim() !== '';
        setHasLMSAccess(hasCredentials);
      } else {
        setHasLMSAccess(false);
      }
    };

    checkAuthStatus();

    // Listen for storage changes (login/logout from other tabs)
    const handleStorageChange = (e) => {
      if (e.key === '_auth_token_' || e.key === '_user_data_') {
        checkAuthStatus();
      }
    };

    // Listen for custom login event (from modal login)
    const handleLoginSuccess = () => {
      checkAuthStatus();
    };

    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('loginSuccess', handleLoginSuccess);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('loginSuccess', handleLoginSuccess);
    };
  }, []);

  // Sync state with body class changes
  useEffect(() => {
    const checkMenuState = () => {
      const hasActiveClass = document.body.classList.contains('mobile-nav-active');
      if (hasActiveClass !== isMenuOpen) {
        setIsMenuOpen(hasActiveClass);
      }
    };

    // Check on mount
    checkMenuState();

    // Listen for class changes on body
    const observer = new MutationObserver(checkMenuState);
    observer.observe(document.body, {
      attributes: true,
      attributeFilter: ['class']
    });

    return () => {
      observer.disconnect();
    };
  }, [isMenuOpen]);

  const handleLogout = () => {
    authService.clearAuthData();
    setIsAuthenticated(false);
    setUserData(null);
    // Redirect to home page
    window.location.href = '/';
  };

  return (
    <>
      <header id="header" className="header d-flex align-items-center sticky-top">
        <div className="container position-relative d-flex align-items-center justify-content-between">
          <Link to="/" className="logo d-flex align-items-center me-auto me-xl-0">
            <img src="/assets/img/mewar-logo.png" alt="Mewar university" />
          </Link>

          <nav id="navmenu" className="navmenu d-flex">
            <ul>
              <li>
                <Link to="/" className={isActive('/')}>
                  Home
                </Link>
              </li>
              
              <li className="dropdown">
                <a href="/courses" onClick={toggleDropdown}>
                  <span>Courses</span> <i className="bi bi-chevron-down toggle-dropdown"></i>
                </a>
                <ul>
                  <li className="dropdown">
                    <a href="/btech-courses" onClick={toggleDropdown}>
                      <span>B-Tech</span> <i className="bi bi-chevron-down toggle-dropdown"></i>
                    </a>
                    <ul className="sub-dropdown">
                      <li><Link to="/btech-civil-engineering" onClick={closeMenu}>Civil Engineering</Link></li>
                      <li><Link to="/btech-computer-science-engineering" onClick={closeMenu}>Computer Science Engineering</Link></li>
                      <li><Link to="/btech-electronic-and-communication-engneering" onClick={closeMenu}>Electronic & Communication Engineering</Link></li>
                      <li><Link to="/btech-electrical-engineering" onClick={closeMenu}>Electrical Engineering</Link></li>
                      <li><Link to="/btech-chemical-engneering" onClick={closeMenu}>Chemical Engineering</Link></li>
                      <li><Link to="/btech-mechanical-engineering" onClick={closeMenu}>Mechanical Engineering</Link></li>
                      <li><Link to="/btech-mining-engneering" onClick={closeMenu}>Mining Engineering</Link></li>
                    </ul>
                  </li>
                  
                  <li className="dropdown">
                    <a href="/mtech-courses" onClick={toggleDropdown}>
                      <span>M-Tech</span> <i className="bi bi-chevron-down toggle-dropdown"></i>
                    </a>
                    <ul className="sub-dropdown">
                      <li><Link to="/mtech-in-civil-engineering" onClick={closeMenu}>Civil Engineering</Link></li>
                      <li><Link to="/mtech-in-computer-science-engineering" onClick={closeMenu}>Computer Science & Engineering</Link></li>
                      <li><Link to="/mtech-in-mechanical-engineering" onClick={closeMenu}>Mechanical Engineering</Link></li>
                      <li><Link to="/mtech-in-electronics-communication-engineering" onClick={closeMenu}>Electronics & Communication Engineering</Link></li>
                      <li><Link to="/mtech-in-electrical-engineering" onClick={closeMenu}>Electrical Engineering</Link></li>
                      <li><Link to="/mtech-in-chemical-engineering" onClick={closeMenu}>Chemical Engineering</Link></li>
                      <li><Link to="/mtech-in-mining-engineering" onClick={closeMenu}>Mining Engineering</Link></li>
                    </ul>
                  </li>
                </ul>
              </li>
              
              <li>
                <Link to="/about-us" className={isActive('/about-us')}>
                  About
                </Link>
              </li>
              <li>
                <Link to="/contact-us" className={isActive('/contact-us')}>
                  Contact
                </Link>
              </li>
              
              {isAuthenticated ? (
                <li className="dropdown">
                  <a href="/dashboard" onClick={toggleDropdown}>
                    <span>Welcome, {userData?.full_name?.split(' ')[0] || 'Student'}</span> <i className="bi bi-chevron-down toggle-dropdown"></i>
                  </a>
                  <ul>
                    <li>
                      <Link to="/application-form" className="text-decoration-none" onClick={closeMenu}>
                        Application Form
                      </Link>
                    </li>
                    <li>
                      <Link to="/course-fees" className="text-decoration-none" onClick={closeMenu}>
                        Course Fees
                      </Link>
                    </li>

                    <li><hr className="dropdown-divider" /></li>
                    <li>
                      <Link to="/payment-history" className="text-decoration-none" onClick={closeMenu}>
                        Payment History
                      </Link>
                    </li>

                    {hasLMSAccess && (
                      <>
                        <li><hr className="dropdown-divider" /></li>
                        <li>
                          <a 
                            href="#"
                            className="text-decoration-none"
                            onClick={(e) => {
                              e.preventDefault();
                              setShowLMSLogin(true);
                            }}
                            role="button"
                            tabIndex="0"
                          >
                            LMS Portal
                          </a>
                        </li>
                      </>
                    )}
                    
                    <li><hr className="dropdown-divider" /></li>
                    <li>
                      <a 
                        href="/logout" 
                        className="text-decoration-none"
                        onClick={(e) => {
                          e.preventDefault();
                          handleLogout();
                        }}
                      >
                        Logout
                      </a>
                    </li>
                  </ul>
                </li>
              ) : (
                <li className="dropdown">
                  <a href="/login" onClick={toggleDropdown}>
                    <span>Login</span> <i className="bi bi-chevron-down toggle-dropdown"></i>
                  </a>
                  <ul>
                    <li>
                      <Link to="/login" className="text-decoration-none" onClick={closeMenu}>
                        Student Login
                      </Link>
                    </li>
                    <li>
                      <a 
                        href="/application-form"
                        className="text-decoration-none"
                        onClick={(e) => {
                          e.preventDefault();
                          setShowLoginModal(true);
                        }}
                      >
                        Application Form
                      </a>
                    </li>
                  </ul>
                </li>
              )}
              
              
            </ul>
            
            <i className={`mobile-nav-toggle d-xl-none bi ${isMenuOpen ? 'bi-x' : 'bi-list'}`} onClick={toggleMenu}></i>
            
            {!isAuthenticated && (
              <button 
                className="btn-getstarted mx-lg-3 d-none d-xl-flex" 
                onClick={() => setShowApplicationModal(true)}
              >
                Apply Now
              </button>
            )}
          </nav>
        </div>
      </header>

      {/* Application Modal */}
      <ApplicationForm 
        show={showApplicationModal} 
        onHide={() => setShowApplicationModal(false)} 
      />

      {/* Application Login Modal */}
      <ApplicationLoginForm 
        show={showLoginModal} 
        onHide={() => setShowLoginModal(false)} 
      />

      {/* LMS Login Handler */}
      {showLMSLogin && (
        <LMSLoginHandler 
          onComplete={() => setShowLMSLogin(false)} 
        />
      )}
    </>
  );
};

export default Header;
